import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import axiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import { 
  FiMail, 
  FiPlus, 
  FiTrash2, 
  FiEye,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiSend,
  FiUpload,
  FiFile
} from 'react-icons/fi';
import EnhancedUniversalTable from '../../components/EnhancedUniversalTable';
import CustomDropdown from '../../components/CustomDropdown';
import './EmailBatch.css';

const EmailBatch = () => {
  const BASE_URL = config.api.baseURL;
  const API_PATH = config.api.apiPath;
  const API_ENDPOINT = `${BASE_URL}/${API_PATH}/email-batches`;

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);
  const [showAddEmailForm, setShowAddEmailForm] = useState(false);
  const [newEmailDetails, setNewEmailDetails] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [pendingAdditions, setPendingAdditions] = useState([]);
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadBatchName, setUploadBatchName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [search, setSearch] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    batchedName: '',
    personDetails: [{ email: '', firstName: '', lastName: '', phone: '' }]
  });

  // Fetch batches
  const fetchBatches = async () => {
    try {
      setLoading(true);
      const params = {
        pageSize: 50,
        pageNumber: 1,
        search: search,
        sortField: 'createdAt',
        sortOrder: 'desc'
      };
      
      if (dateFilter && dateFilter.value) {
        // Handle date filter based on selected option
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfDay = new Date(today);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);
        
        switch (dateFilter.value) {
          case 'today':
            params.startDate = startOfDay.toISOString();
            params.endDate = endOfDay.toISOString();
            break;
          case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStart = new Date(yesterday);
            yesterdayStart.setHours(0, 0, 0, 0);
            const yesterdayEnd = new Date(yesterday);
            yesterdayEnd.setHours(23, 59, 59, 999);
            params.startDate = yesterdayStart.toISOString();
            params.endDate = yesterdayEnd.toISOString();
            break;
          case 'last7days':
            const last7Days = new Date(today);
            last7Days.setDate(last7Days.getDate() - 7);
            params.startDate = last7Days.toISOString();
            params.endDate = endOfDay.toISOString();
            break;
          case 'last30days':
            const last30Days = new Date(today);
            last30Days.setDate(last30Days.getDate() - 30);
            params.startDate = last30Days.toISOString();
            params.endDate = endOfDay.toISOString();
            break;
          case 'thisMonth':
            const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            firstDayOfMonth.setHours(0, 0, 0, 0);
            params.startDate = firstDayOfMonth.toISOString();
            params.endDate = endOfDay.toISOString();
            break;
          case 'lastMonth':
            const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            firstDayLastMonth.setHours(0, 0, 0, 0);
            const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            lastDayLastMonth.setHours(23, 59, 59, 999);
            params.startDate = firstDayLastMonth.toISOString();
            params.endDate = lastDayLastMonth.toISOString();
            break;
          default:
            break;
        }
      }

      const response = await axiosInstance.get(API_ENDPOINT, { params });
      
      if (response && response.data && response.data.statusCode === 200) {
        setBatches(response.data.data || []);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, [dateFilter]);

  // Handle create batch
  const handleCreate = async () => {
    try {
      // Validate
      if (!formData.batchedName) {
        toast.error('Batch name is required');
        return;
      }

      const validPersons = formData.personDetails.filter(
        p => p.email && p.firstName && p.lastName
      );

      if (validPersons.length === 0) {
        toast.error('At least one person with email, firstName, and lastName is required');
        return;
      }

      const batchData = {
        batchedName: formData.batchedName,
        personDetails: validPersons
      };

      const response = await axiosInstance.post(API_ENDPOINT, batchData);
      
      if (response.data.statusCode === 201) {
        toast.success('Batch created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchBatches();
      }
    } catch (error) {
      console.error('Error creating batch:', error);
      toast.error(error.response?.data?.message || 'Failed to create batch');
    }
  };


  // Handle delete batch
  const handleDelete = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`${API_ENDPOINT}/${batchId}`);
      
      if (response.data.statusCode === 200) {
        toast.success('Batch deleted successfully');
        fetchBatches();
      }
    } catch (error) {
      console.error('Error deleting batch:', error);
      toast.error('Failed to delete batch');
    }
  };

  // Handle add email to pending list
  const handleAddEmailToPending = () => {
    try {
      // Validate
      if (!newEmailDetails.email || !newEmailDetails.firstName || !newEmailDetails.lastName) {
        toast.error('Email, First Name, and Last Name are required');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmailDetails.email)) {
        toast.error('Please enter a valid email address');
        return;
      }

      // Get current batch and pending
      const currentPersonDetails = selectedBatch.personDetails || [];
      const allEmails = [
        ...currentPersonDetails.map(p => p.email.toLowerCase()),
        ...pendingAdditions.map(p => p.email.toLowerCase())
      ];
      
      // Check if email already exists
      if (allEmails.includes(newEmailDetails.email.toLowerCase())) {
        toast.error('This email already exists in the batch or pending additions');
        return;
      }

      // Add to pending additions
      const newPending = {
        email: newEmailDetails.email.toLowerCase(),
        firstName: newEmailDetails.firstName.trim(),
        lastName: newEmailDetails.lastName.trim(),
        phone: newEmailDetails.phone || '',
        status: 'pending',
        isPending: true
      };

      setPendingAdditions([...pendingAdditions, newPending]);
      setNewEmailDetails({ email: '', firstName: '', lastName: '', phone: '' });
    } catch (error) {
      console.error('Error adding email to pending:', error);
      toast.error('Failed to add email to pending list');
    }
  };

  // Handle remove from pending additions
  const handleRemoveFromPending = (emailToRemove) => {
    setPendingAdditions(pendingAdditions.filter(p => p.email !== emailToRemove));
  };

  // Handle mark email for deletion
  const handleMarkEmailForDeletion = (emailToDelete) => {
    // Check if already marked
    if (pendingDeletions.includes(emailToDelete.toLowerCase())) {
      return;
    }

    setPendingDeletions([...pendingDeletions, emailToDelete.toLowerCase()]);
  };

  // Handle unmark email from deletion
  const handleUnmarkEmailFromDeletion = (emailToUnmark) => {
    setPendingDeletions(pendingDeletions.filter(e => e !== emailToUnmark.toLowerCase()));
  };

  // Handle update batch with all pending changes
  const handleUpdateBatch = async () => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Updating batch...');

      // Get current batch
      const currentPersonDetails = selectedBatch.personDetails || [];
      
      // Remove emails marked for deletion
      let updatedPersonDetails = currentPersonDetails.filter(
        p => !pendingDeletions.includes(p.email.toLowerCase())
      );

      // Add pending additions
      updatedPersonDetails = [
        ...updatedPersonDetails,
        ...pendingAdditions.map(p => ({
          email: p.email,
          firstName: p.firstName,
          lastName: p.lastName,
          phone: p.phone,
          status: 'pending'
        }))
      ];

      // Check if at least one person remains
      if (updatedPersonDetails.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('Cannot update. At least one person detail is required in the batch');
        return;
      }

      // Update batch
      const response = await axiosInstance.put(`${API_ENDPOINT}/${selectedBatch.batchId}`, {
        personDetails: updatedPersonDetails
      });

      if (response.data.statusCode === 200) {
        toast.dismiss(loadingToast);
        const addedCount = pendingAdditions.length;
        const deletedCount = pendingDeletions.length;
        let message = 'Batch updated successfully';
        if (addedCount > 0 && deletedCount > 0) {
          message = `Batch updated: ${addedCount} email(s) added, ${deletedCount} email(s) removed`;
        } else if (addedCount > 0) {
          message = `${addedCount} email(s) added successfully`;
        } else if (deletedCount > 0) {
          message = `${deletedCount} email(s) removed successfully`;
        }
        toast.success(message);
        
        // Clear pending changes
        setPendingAdditions([]);
        setPendingDeletions([]);
        setShowAddEmailForm(false);
        
        // Refresh batch data
        const updatedBatch = await axiosInstance.get(`${API_ENDPOINT}/${selectedBatch.batchId}`);
        if (updatedBatch.data.statusCode === 200) {
          setSelectedBatch(updatedBatch.data.data);
        }
        fetchBatches();
      }
    } catch (error) {
      console.error('Error updating batch:', error);
      toast.error(error.response?.data?.message || 'Failed to update batch');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      batchedName: '',
      personDetails: [{ email: '', firstName: '', lastName: '', phone: '' }]
    });
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(ext)) {
        toast.error('Please select an Excel (.xlsx, .xls) or CSV (.csv) file');
        return;
      }
      setUploadFile(file);
    }
  };

  // Handle sheet upload
  const handleSheetUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    // Batch name will always come from sheet
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      if (updateMode && selectedBatch) {
        formData.append('updateMode', 'update');
        formData.append('batchId', selectedBatch.batchId);
      }

      const response = await axiosInstance.post(
        `${API_ENDPOINT}/upload-sheet`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.statusCode === 200 || response.data.statusCode === 201) {
        const stats = response.data.stats || {};
        const errors = response.data.errors || [];
        
        // Store upload result
        setUploadResult({
          success: true,
          message: response.data.message,
          stats: stats,
          errors: errors,
          data: response.data.data
        });
        
        // Show only one toast
        if (errors.length > 0 || stats.errors > 0 || stats.duplicateEmails > 0) {
          toast.warning('Upload completed with some issues. Check details in the result modal.');
        } else {
          toast.success(response.data.message);
        }
        
        // Reset upload state
        setUploadFile(null);
        setShowUploadModal(false);
        setUpdateMode(false);
        setSelectedBatch(null);
        
        // Show result modal
        setShowUploadResultModal(true);
        
        // Refresh batches
        fetchBatches();
      }
    } catch (error) {
      console.error('Error uploading sheet:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload sheet';
      const errors = error.response?.data?.errors || [];
      const stats = error.response?.data?.stats || {};
      
      // Store error result
      setUploadResult({
        success: false,
        message: errorMessage,
        stats: stats,
        errors: errors
      });
      
      // Show only one toast
      toast.error(errorMessage);
      
      // Show result modal even on error
      setShowUploadResultModal(true);
    } finally {
      setUploading(false);
    }
  };

  // Open upload modal for update
  const handleOpenUploadForUpdate = (batch) => {
    setSelectedBatch(batch);
    setUpdateMode(true);
    setShowUploadModal(true);
  };

  // Download sample sheet
  const handleDownloadSample = async (format = 'xlsx') => {
    try {
      // Only headers, no data rows
      const headers = ['Batch Name', 'Email', 'First Name', 'Last Name', 'Phone'];
      
      // Create a new workbook using ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Email Batch');

      // Set column widths
      worksheet.columns = [
        { header: 'Batch Name', key: 'batchName', width: 25 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'First Name', key: 'firstName', width: 15 },
        { header: 'Last Name', key: 'lastName', width: 15 },
        { header: 'Phone', key: 'phone', width: 15 }
      ];

      // IMPORTANT: First, unlock ALL cells by default (Excel locks all by default)
      // We need to create cells and unlock them explicitly
      // Create and unlock cells for rows 2-1000 (data entry area)
      for (let rowNum = 2; rowNum <= 1000; rowNum++) {
        const row = worksheet.getRow(rowNum);
        // Create cells for all 5 columns (Batch Name, Email, First Name, Last Name, Phone)
        for (let colNum = 1; colNum <= 5; colNum++) {
          const cell = row.getCell(colNum);
          // Explicitly unlock the cell
          cell.protection = { locked: false };
        }
      }

      // Add header row
      const headerRow = worksheet.getRow(1);
      headerRow.values = headers;
      
      // Style header row - bold, blue background, white text
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' }
      };
      headerRow.height = 20;

      // Lock ONLY header row (row 1) cells - make them non-editable
      headerRow.eachCell((cell) => {
        cell.protection = { locked: true };
      });

      // Freeze the first row (header row)
      worksheet.views = [{
        state: 'frozen',
        ySplit: 1, // Freeze first row
        topLeftCell: 'A2',
        activeCell: 'A2'
      }];

      // Protect the sheet - this enforces the cell protection
      // Only locked cells (header row) will be protected
      // Empty password string means no password required
      await worksheet.protect('', {
        selectLockedCells: false, // Prevent selecting/clicking locked cells (headers)
        selectUnlockedCells: true, // Allow selecting unlocked cells (data rows)
        formatCells: false, // Prevent formatting locked cells
        formatColumns: false,
        formatRows: false,
        insertColumns: true, // Allow inserting columns
        insertRows: true, // Allow inserting rows
        insertHyperlinks: true,
        deleteColumns: true,
        deleteRows: true,
        sort: true,
        autoFilter: true,
        pivotTables: true,
        // Make sure unlocked cells can be edited
        editObjects: true,
        editScenarios: true
      });

      // Generate Excel file buffer
      const buffer = await workbook.xlsx.writeBuffer();

      // Create blob and download
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'email-batch-sample.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Sample sheet downloaded successfully! Only header row is locked.');
    } catch (error) {
      console.error('Error downloading sample sheet:', error);
      toast.error('Failed to download sample sheet');
    }
  };

  // Add person detail row
  const addPersonDetail = () => {
    setFormData({
      ...formData,
      personDetails: [
        ...formData.personDetails,
        { email: '', firstName: '', lastName: '', phone: '' }
      ]
    });
  };

  // Remove person detail row
  const removePersonDetail = (index) => {
    const newPersonDetails = formData.personDetails.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      personDetails: newPersonDetails
    });
  };

  // Update person detail
  const updatePersonDetail = (index, field, value) => {
    const newPersonDetails = [...formData.personDetails];
    newPersonDetails[index][field] = value;
    setFormData({
      ...formData,
      personDetails: newPersonDetails
    });
  };

  // Table columns
  const columns = [
    { 
      key: 'batchedName', 
      title: 'Batch Name', 
      sortable: true,
      render: (value) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px'
        }}>
          <FiMail style={{ color: '#6b7280', fontSize: '16px' }} />
          <span style={{ 
            fontWeight: '500', 
            color: '#111827',
            fontSize: '0.9rem'
          }}>{value}</span>
        </div>
      )
    },
    { 
      key: 'createdAt', 
      title: 'Created', 
      type: 'date',
      sortable: true,
      render: (value) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px'
        }}>
          <FiAlertCircle style={{ color: '#6b7280', fontSize: '14px' }} />
          <span style={{ 
            fontSize: '0.85rem',
            fontWeight: '500',
            color: '#111827'
          }}>{new Date(value).toLocaleDateString()}</span>
        </div>
      )
    }
  ];

  // Table data
  const tableData = batches.map(batch => {
    const personDetails = batch.personDetails || [];
    
    return {
      id: batch.batchId,
      batchedName: batch.batchedName,
      totalRecipients: batch.totalRecipients || personDetails.length || 0,
      createdAt: batch.createdAt
    };
  });

  // Status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: '#6c757d', icon: <FiAlertCircle /> },
      sending: { color: '#007bff', icon: <FiSend /> },
      sent: { color: '#28a745', icon: <FiCheckCircle /> },
      failed: { color: '#dc3545', icon: <FiXCircle /> },
      cancelled: { color: '#ffc107', icon: <FiXCircle /> }
    };

    const config = statusConfig[status] || statusConfig.draft;
    
    return (
      <span 
        className="status-badge" 
        style={{ 
          backgroundColor: `${config.color}20`,
          color: config.color,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="email-batch-container">
      <div className="email-batch-header">
        <div className="header-left">
          <h1>
            <FiMail className="header-icon" />
            Email Batches
          </h1>
          <p>Manage and send emails to multiple recipients with personal details</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="date-filter-wrapper">
          <CustomDropdown
            options={[
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'last7days', label: 'Last 7 Days' },
              { value: 'last30days', label: 'Last 30 Days' },
              { value: 'thisMonth', label: 'This Month' },
              { value: 'lastMonth', label: 'Last Month' }
            ]}
            value={dateFilter}
            onChange={(option) => setDateFilter(option)}
            placeholder="All Dates"
            className="date-filter-dropdown"
            textColor="#ffffff"
          />
        </div>
        <div className="filter-actions">
          <button 
            className="btn-secondary"
            onClick={() => {
              setUpdateMode(false);
              setSelectedBatch(null);
              setUploadFile(null);
              setShowUploadModal(true);
            }}
          >
            <FiUpload /> Upload Sheet
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <FiPlus /> Create Batch
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">Loading batches...</div>
        ) : (
          <EnhancedUniversalTable
            data={tableData}
            columns={columns}
            loading={loading}
            onRowClick={(row) => {
              const batch = batches.find(b => b.batchId === row.id);
              setSelectedBatch(batch);
              setShowDetailsModal(true);
            }}
            actions={[
              {
                key: 'view',
                icon: <FiEye />,
                title: 'View Details',
                variant: 'primary',
                show: () => true
              },
              {
                key: 'upload',
                icon: <FiUpload />,
                title: 'Upload Sheet',
                variant: 'info',
                show: (row) => {
                  const batch = batches.find(b => b.batchId === row.id);
                  return batch && batch.status !== 'sent';
                }
              },
              {
                key: 'addEmail',
                icon: <FiPlus />,
                title: 'Add Email',
                variant: 'success',
                show: () => true
              },
              {
                key: 'delete',
                icon: <FiTrash2 />,
                title: 'Delete Batch',
                variant: 'danger',
                show: () => true
              }
            ]}
            onActionClick={(actionKey, row) => {
              const batch = batches.find(b => b.batchId === row.id);
              if (actionKey === 'view') {
                setSelectedBatch(batch);
                setShowDetailsModal(true);
              } else if (actionKey === 'upload') {
                handleOpenUploadForUpdate(batch);
              } else if (actionKey === 'addEmail') {
                setSelectedBatch(batch);
                setShowAddEmailForm(true);
                setShowDetailsModal(true);
              } else if (actionKey === 'delete') {
                handleDelete(row.id);
              }
            }}
            searchable={true}
            filterable={false}
            sortable={true}
            pagination={true}
            pageSize={10}
            emptyMessage="No email batches found"
          />
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Email Batch</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Batch Name *</label>
                <input
                  type="text"
                  value={formData.batchedName}
                  onChange={(e) => setFormData({ ...formData, batchedName: e.target.value })}
                  placeholder="Enter batch name"
                  required
                />
              </div>

              <div className="form-group">
                <div className="form-group-header">
                  <label>Person Details *</label>
                  <button type="button" className="btn-add" onClick={addPersonDetail}>
                    <FiPlus /> Add Person
                  </button>
                </div>
                
                <div className="person-details-list">
                  {formData.personDetails.map((person, index) => (
                    <div key={index} className="person-detail-row">
                      <div className="row-header">
                        <span>Person {index + 1}</span>
                        {formData.personDetails.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => removePersonDetail(index)}
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                      <div className="row-fields">
                        <input
                          type="email"
                          placeholder="Email *"
                          value={person.email}
                          onChange={(e) => updatePersonDetail(index, 'email', e.target.value)}
                          required
                        />
                        <input
                          type="text"
                          placeholder="First Name *"
                          value={person.firstName}
                          onChange={(e) => updatePersonDetail(index, 'firstName', e.target.value)}
                          required
                        />
                        <input
                          type="text"
                          placeholder="Last Name *"
                          value={person.lastName}
                          onChange={(e) => updatePersonDetail(index, 'lastName', e.target.value)}
                          required
                        />
                        <input
                          type="text"
                          placeholder="Phone"
                          value={person.phone}
                          onChange={(e) => updatePersonDetail(index, 'phone', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreate}>
                Create Batch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedBatch && (
        <div className="modal-overlay" onClick={() => {
          setShowDetailsModal(false);
          setShowAddEmailForm(false);
          setNewEmailDetails({ email: '', firstName: '', lastName: '', phone: '' });
          setPendingAdditions([]);
          setPendingDeletions([]);
        }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Email</h2>
              <button className="close-btn" onClick={() => {
                setShowDetailsModal(false);
                setShowAddEmailForm(false);
                setNewEmailDetails({ email: '', firstName: '', lastName: '', phone: '' });
                setPendingAdditions([]);
                setPendingDeletions([]);
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="details-section">
                <h3>Batch Information( {selectedBatch.batchedName.toUpperCase()} )</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Total Recipients:</label>
                    <span>{selectedBatch.totalRecipients || selectedBatch.personDetails?.length || 0}</span>
                  </div>
                  <div className="info-item">
                    <label>Created:</label>
                    <span>{new Date(selectedBatch.createdAt).toLocaleString()}</span>
                  </div>
                  {selectedBatch.sentDate && (
                    <div className="info-item">
                      <label>Sent Date:</label>
                      <span>{new Date(selectedBatch.sentDate).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedBatch.completedDate && (
                    <div className="info-item">
                      <label>Completed Date:</label>
                      <span>{new Date(selectedBatch.completedDate).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="details-section">
                <div className="section-header-with-action">
                  <h3>Person Details</h3>
                  {selectedBatch.status !== 'sent' && (
                    <button 
                      className={`btn-toggle-form ${showAddEmailForm ? 'active' : ''}`}
                      onClick={() => setShowAddEmailForm(!showAddEmailForm)}
                    >
                      <FiPlus /> {showAddEmailForm ? 'Cancel' : 'Add Email'}
                    </button>
                  )}
                </div>

                {/* Add Email Form */}
                {showAddEmailForm && selectedBatch.status !== 'sent' && (
                  <div className="add-email-form-container">
                    <div className="add-email-form-header">
                      <h4>Add New Email</h4>
                    </div>
                    <div className="add-email-form-body">
                      <div className="form-row">
                        <div className="form-field">
                          <label>Email *</label>
                          <input
                            type="email"
                            placeholder="Enter email address"
                            value={newEmailDetails.email}
                            onChange={(e) => setNewEmailDetails({ ...newEmailDetails, email: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-field">
                          <label>First Name *</label>
                          <input
                            type="text"
                            placeholder="Enter first name"
                            value={newEmailDetails.firstName}
                            onChange={(e) => setNewEmailDetails({ ...newEmailDetails, firstName: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-field">
                          <label>Last Name *</label>
                          <input
                            type="text"
                            placeholder="Enter last name"
                            value={newEmailDetails.lastName}
                            onChange={(e) => setNewEmailDetails({ ...newEmailDetails, lastName: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-field">
                          <label>Phone</label>
                          <input
                            type="text"
                            placeholder="Enter phone number (optional)"
                            value={newEmailDetails.phone}
                            onChange={(e) => setNewEmailDetails({ ...newEmailDetails, phone: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="add-email-form-footer">
                      <button 
                        className="btn-primary"
                        onClick={handleAddEmailToPending}
                      >
                        <FiPlus /> Add
                      </button>
                      <button 
                        className="btn-secondary"
                        onClick={() => {
                          setShowAddEmailForm(false);
                          setNewEmailDetails({ email: '', firstName: '', lastName: '', phone: '' });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* New Emails List */}
                {pendingAdditions.length > 0 && (
                  <div className="pending-section" style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    border: '1px solid #fbbf24'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <strong style={{ color: '#92400e', fontSize: '13px' }}>New Emails ({pendingAdditions.length})</strong>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {pendingAdditions.map((pending, index) => (
                        <span key={index} style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          backgroundColor: '#ffffff',
                          borderRadius: '4px',
                          fontSize: '12px',
                          border: '1px solid #fbbf24'
                        }}>
                          {pending.email}
                          <button
                            onClick={() => handleRemoveFromPending(pending.email)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc2626',
                              cursor: 'pointer',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Remove from pending"
                          >
                            <FiXCircle size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Emails to Remove List */}
                {pendingDeletions.length > 0 && (
                  <div className="pending-section" style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    border: '1px solid #f87171'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <strong style={{ color: '#991b1b', fontSize: '13px' }}>Emails to Remove ({pendingDeletions.length})</strong>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {pendingDeletions.map((email, index) => {
                        const person = selectedBatch.personDetails?.find(p => p.email.toLowerCase() === email);
                        return (
                          <span key={index} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: '#ffffff',
                            borderRadius: '4px',
                            fontSize: '12px',
                            border: '1px solid #f87171'
                          }}>
                            {email}
                            <button
                              onClick={() => handleUnmarkEmailFromDeletion(email)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#16a34a',
                                cursor: 'pointer',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Unmark from deletion"
                            >
                              <FiCheckCircle size={14} />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="person-details-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Phone</th>
                        {selectedBatch.status !== 'sent' && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBatch.personDetails?.map((person, index) => {
                        const isMarkedForDeletion = pendingDeletions.includes(person.email.toLowerCase());
                        return (
                          <tr 
                            key={index}
                            style={isMarkedForDeletion ? {
                              backgroundColor: '#fee2e2',
                              opacity: 0.7,
                              textDecoration: 'line-through'
                            } : {}}
                          >
                            <td>{person.email}</td>
                            <td>{person.firstName} {person.lastName}</td>
                            <td>{person.phone || '-'}</td>
                            {selectedBatch.status !== 'sent' && (
                              <td>
                                {isMarkedForDeletion ? (
                                  <button
                                    className="btn-undo-delete"
                                    onClick={() => handleUnmarkEmailFromDeletion(person.email)}
                                    title="Undo Delete"
                                  >
                                    <FiCheckCircle />
                                  </button>
                                ) : (
                                  <button
                                    className="btn-delete-email"
                                    onClick={() => handleMarkEmailForDeletion(person.email)}
                                    title="Mark for Deletion"
                                  >
                                    <FiTrash2 />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div>
                {(pendingAdditions.length > 0 || pendingDeletions.length > 0) && selectedBatch.status !== 'sent' && (
                  <button 
                    className="btn-primary"
                    onClick={handleUpdateBatch}
                  >
                    Update Batch ({pendingAdditions.length + pendingDeletions.length} changes)
                  </button>
                )}
              </div>
              <button className="btn-secondary" onClick={() => {
                setShowDetailsModal(false);
                setShowAddEmailForm(false);
                setNewEmailDetails({ email: '', firstName: '', lastName: '', phone: '' });
                setPendingAdditions([]);
                setPendingDeletions([]);
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Sheet Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => {
          if (!uploading) {
            setShowUploadModal(false);
            setUploadFile(null);
            setUpdateMode(false);
            setSelectedBatch(null);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiUpload style={{ marginRight: '8px' }} />
                {updateMode ? 'Update Batch from Sheet' : 'Create Batch from Sheet'}
              </h2>
              <button 
                className="close-btn" 
                onClick={() => {
                  if (!uploading) {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUpdateMode(false);
                    setSelectedBatch(null);
                  }
                }}
                disabled={uploading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Upload File *</label>
                <div style={{
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'center',
                  backgroundColor: '#f9fafb',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.6 : 1
                }}>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    style={{ display: 'none' }}
                    id="file-upload-input"
                  />
                  <label 
                    htmlFor="file-upload-input"
                    style={{ 
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    {uploadFile ? (
                      <>
                        <FiFile style={{ fontSize: '48px', color: '#667eea' }} />
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                            {uploadFile.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {(uploadFile.size / 1024).toFixed(2)} KB
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadFile(null);
                            document.getElementById('file-upload-input').value = '';
                          }}
                          disabled={uploading}
                          style={{ marginTop: '8px' }}
                        >
                          Change File
                        </button>
                      </>
                    ) : (
                      <>
                        <FiUpload style={{ fontSize: '48px', color: '#9ca3af' }} />
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                            Click to upload or drag and drop
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            Excel (.xlsx, .xls) or CSV (.csv) files only
                          </div>
                        </div>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '16px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontWeight: '600', color: '#0369a1' }}>
                    File Format Requirements:
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownloadSample('xlsx')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: '#0369a1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#0284c7'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#0369a1'}
                  >
                    <FiFile style={{ fontSize: '14px' }} />
                    Download Sample
                  </button>
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#075985', fontSize: '14px' }}>
                  <li><strong>Required columns:</strong> <strong>Batch Name</strong>, <strong>Email</strong>, <strong>First Name</strong>, <strong>Last Name</strong></li>
                  <li><strong>Optional column:</strong> <strong>Phone</strong></li>
                  <li>Column names are case-insensitive (e.g., "Email", "email", "E-MAIL" all work)</li>
                  <li><strong>Batch Name must be included in the sheet</strong> - it will be used to find or create the batch</li>
                  <li>If batch name exists in DB, duplicate emails will be shown as errors and new emails will be added</li>
                  <li>If batch name doesn't exist, a new batch will be created</li>
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  if (!uploading) {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUpdateMode(false);
                    setSelectedBatch(null);
                  }
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSheetUpload}
                disabled={uploading || !uploadFile}
              >
                {uploading ? 'Uploading...' : (updateMode ? 'Update Batch' : 'Create Batch')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Result Modal */}
      {showUploadResultModal && uploadResult && (
        <div className="modal-overlay" onClick={() => setShowUploadResultModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {uploadResult.success ? (
                  <span style={{ color: '#10b981' }}>✓ Upload Result</span>
                ) : (
                  <span style={{ color: '#ef4444' }}>✗ Upload Failed</span>
                )}
              </h2>
              <button className="close-btn" onClick={() => setShowUploadResultModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="details-section">
                <h3 style={{ 
                  color: uploadResult.success ? '#10b981' : '#ef4444',
                  marginBottom: '16px'
                }}>
                  {uploadResult.message}
                </h3>

                {/* Statistics */}
                {uploadResult.stats && (
                  <div className="info-grid" style={{ marginBottom: '24px' }}>
                    {/* 1. Total Rows */}
                    <div className="info-item" style={{ 
                      backgroundColor: '#e0e7ff', 
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #6366f1'
                    }}>
                      <label style={{ fontWeight: '600', color: '#3730a3' }}>Total Rows:</label>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#6366f1' }}>
                        {uploadResult.stats.totalRows || 0}
                      </span>
                    </div>
                    
                    {/* 2. Data Inserted - Only show if success and data was actually inserted */}
                    <div className="info-item" style={{ 
                      backgroundColor: '#d1fae5', 
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #10b981'
                    }}>
                      <label style={{ fontWeight: '600', color: '#065f46' }}>Data Inserted:</label>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
                        {uploadResult.success && uploadResult.stats ? 
                          (uploadResult.stats.newEmails !== undefined ? uploadResult.stats.newEmails : (uploadResult.stats.validPersons || 0)) 
                          : 0}
                      </span>
                    </div>
                    
                    {/* 3. Duplicate Emails */}
                    <div className="info-item" style={{ 
                      backgroundColor: '#fef3c7', 
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #f59e0b'
                    }}>
                      <label style={{ fontWeight: '600', color: '#92400e' }}>Duplicate Emails:</label>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#f59e0b' }}>
                        {uploadResult.stats.duplicateEmails || 0}
                      </span>
                    </div>
                    
                    {/* 4. Errors Found */}
                    <div className="info-item" style={{ 
                      backgroundColor: '#fee2e2', 
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #ef4444'
                    }}>
                      <label style={{ fontWeight: '600', color: '#991b1b' }}>Errors Found:</label>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#ef4444' }}>
                        {(uploadResult.errors && uploadResult.errors.length > 0) ? uploadResult.errors.length : (uploadResult.stats && uploadResult.stats.errors) || 0}
                      </span>
                    </div>
                  </div>
                )}

                {/* Error Details */}
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="details-section">
                    <h4 style={{ 
                      color: '#ef4444', 
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FiAlertCircle /> Error Details ({uploadResult.errors.length} errors)
                    </h4>
                    <div style={{
                      maxHeight: '400px',
                      overflowY: 'auto',
                      border: '1px solid #fee2e2',
                      borderRadius: '8px',
                      backgroundColor: '#fef2f2',
                      padding: '12px'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #fecaca' }}>
                            <th style={{ 
                              padding: '8px', 
                              textAlign: 'left', 
                              color: '#991b1b',
                              fontWeight: '600'
                            }}>Row</th>
                            <th style={{ 
                              padding: '8px', 
                              textAlign: 'left', 
                              color: '#991b1b',
                              fontWeight: '600'
                            }}>Error Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResult.errors.map((error, index) => {
                            // Extract row number from error message
                            const rowMatch = error.match(/Row (\d+)/i);
                            const rowNumber = rowMatch ? rowMatch[1] : (error.includes('Batch Name') ? 'N/A' : index + 1);
                            // For batch name errors, show the full message, for row errors, remove Row prefix
                            const errorMessage = error.includes('Batch Name') 
                              ? error 
                              : error.replace(/Row \d+:\s*/i, '').trim();
                            
                            return (
                              <tr key={index} style={{ 
                                borderBottom: '1px solid #fecaca',
                                backgroundColor: index % 2 === 0 ? '#fff' : '#fef2f2'
                              }}>
                                <td style={{ 
                                  padding: '8px', 
                                  fontWeight: '600',
                                  color: '#991b1b',
                                  width: '80px'
                                }}>
                                  {rowNumber}
                                </td>
                                <td style={{ padding: '8px', color: '#7f1d1d' }}>
                                  {errorMessage}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Success message - only show if success AND data was actually inserted */}
                {uploadResult.success && 
                 (!uploadResult.errors || uploadResult.errors.length === 0) && 
                 (uploadResult.stats?.newEmails > 0 || uploadResult.stats?.validPersons > 0) && (
                  <div style={{
                    padding: '20px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '8px',
                    border: '1px solid #10b981',
                    textAlign: 'center',
                    color: '#065f46',
                    fontWeight: '500'
                  }}>
                    ✓ All data inserted successfully! No errors found.
                  </div>
                )}
                
                {/* Error message - show if upload failed OR no data was inserted */}
                {(!uploadResult.success || 
                  (uploadResult.stats && uploadResult.stats.newEmails === 0 && uploadResult.stats.validPersons === 0)) && (
                  <div style={{
                    padding: '20px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '8px',
                    border: '1px solid #ef4444',
                    textAlign: 'center',
                    color: '#991b1b',
                    fontWeight: '500',
                    marginTop: uploadResult.errors && uploadResult.errors.length > 0 ? '0' : '16px'
                  }}>
                    {!uploadResult.success 
                      ? '✗ Upload failed. Please check the errors above and try again.'
                      : '✗ No data was inserted. Please check the errors above and fix the issues in your sheet.'}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-primary" 
                onClick={() => {
                  setShowUploadResultModal(false);
                  setUploadResult(null);
                }}
                style={{ marginLeft: 'auto' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailBatch;

